import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { ApiError } from '../api/client'
import {
  rustdeskLaunchQuery,
  rustdeskLaunchState,
  startRustdeskLaunch,
  type RustdeskLaunchAccount,
  type RustdeskLaunchPage as Page,
  type RustdeskLaunchState,
} from '../api/remoteConnectRustdesk'
import { Forbidden } from '../ui/Forbidden'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import { RustdeskInstallWait } from './RustdeskInstallProgress'
import './RustdeskLaunchPage.css'

/**
 * Lansarea unei conexiuni RustDesk - macheta
 * `views/version-3/remote_connect/rustdesk_launch.php` si scriptul ei
 * `public/js/views/remote_connect/rustdesk_launch-1.js`, mutate.
 *
 * Pagina primeste de la server masina, identitatea raportata de agent (sau
 * null), conturile de administrare (FARA parola), kiturile.
 *
 * De pe 12 octombrie 2026 (CEC 64) nu mai vine nicio parola permanenta.
 * Butonul "Get one-time password" cere intrarea (`/start`); serverul face o
 * parola de unica folosinta, agentul o pune in RustDesk, iar pagina o primeste
 * O SINGURA DATA, cand agentul a confirmat-o. Parola sta numai in memoria
 * paginii - nu in cache, nu in localStorage - si expira daca nu incepe o
 * sesiune; la sfarsitul sesiunii agentul o roteste.
 *
 * Adresa e cea din portal, sub /app: `remote_connect/rustdesk_launch/{device}/
 * {ticket}` - butonul Connect din tichet o deschide in fila noua, cum facea
 * portalul cu `hostUrlJoin`.
 */

const DOTS = '••••••••'

/** `navigator.clipboard` cu caderea pe `execCommand`, ca in scriptul vechi. */
function copyText(text: string): void {
  const done = () => toast('Copied.', '', 'success')
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(done, done)
    return
  }
  const tmp = document.createElement('textarea')
  tmp.value = text
  document.body.appendChild(tmp)
  tmp.select()
  document.execCommand('copy')
  tmp.remove()
  done()
}

function CopyButton({ text, label }: { text: string; label: string }) {
  return (
    <button type="button" className="btn btn-default btn-xs rdl-copy" data-copy={text} onClick={() => copyText(text)}>
      <i className="far fa-copy" aria-hidden="true"></i> {label}
    </button>
  )
}

/** O parola ascunsa sub puncte, cu butonul Show/Hide - `#rdlShow` / `.rdl-secret-show`. */
function Secret({ value, id, wide }: { value: string; id?: string; wide?: boolean }) {
  const [shown, setShown] = useState(false)
  return (
    <>
      <span className={`rdl-pass${id ? '' : ' rdl-secret'}`} id={id} data-pass={value}>
        {shown ? value : DOTS}
      </span>
      <button
        type="button"
        className={`btn btn-default btn-xs${id ? '' : ' rdl-secret-show'}`}
        id={id ? `${id}Show` : undefined}
        onClick={() => setShown((v) => !v)}
      >
        <i className={`far ${shown ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true"></i>
        {wide ? (shown ? ' Hide' : ' Show') : ''}
      </button>
    </>
  )
}

function AdminAccount({ account }: { account: RustdeskLaunchAccount }) {
  return (
    <div className="rdl-row">
      <span className="rdl-label" title="Administrator account for elevation (UAC) inside the session">
        <i className="fas fa-user-shield" aria-hidden="true"></i> {account.label || 'Admin account'}
      </span>
      <span className="rdl-pass">{account.username}</span>
      <CopyButton text={account.username} label="User" />
      <span className="text-muted rdl-vault" title="The password is never shown; it stays in the vault.">
        <i className="fas fa-lock" aria-hidden="true"></i> held in {account.vault === 'hashicorp' ? 'HashiCorp Vault' : 'the platform vault'}
      </span>
    </div>
  )
}

/** Stari in care nu mai e nimic de asteptat. */
const DONE = new Set(['armed', 'revealed', 'ready', 'expired', 'failed', 'closed'])

/** Dupa atat fara raspuns de la agent, se spune ca s-ar putea sa fie vechi. */
const SLOW_AFTER_MS = 60_000

function OneTimePassword({ page, rustdeskId }: { page: Page; rustdeskId: string }) {
  const [launch, setLaunch] = useState<RustdeskLaunchState | null>(null)
  const [password, setPassword] = useState<string | null>(null)
  const [stealth, setStealth] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [slow, setSlow] = useState(false)
  const started = useRef(0)

  const waiting = launch !== null && !DONE.has(launch.state)

  useEffect(() => {
    if (!waiting || launch === null) return
    const timer = window.setInterval(() => {
      rustdeskLaunchState(page.device.id, launch.launch).then(
        (next) => {
          if (next.password) setPassword(next.password)
          setLaunch(next)
          if (Date.now() - started.current > SLOW_AFTER_MS) setSlow(true)
        },
        (e: unknown) => setError(e instanceof Error ? e.message : String(e)),
      )
    }, 2000)
    return () => window.clearInterval(timer)
  }, [waiting, launch, page.device.id])

  const start = () => {
    setBusy(true)
    setError('')
    setPassword(null)
    setSlow(false)
    started.current = Date.now()
    startRustdeskLaunch(page.device.id, page.ticket ?? 0, stealth).then(
      (answer) => {
        setLaunch(answer)
        setBusy(false)
      },
      (e: unknown) => {
        setError(e instanceof Error ? e.message : String(e))
        setBusy(false)
      },
    )
  }

  const openUrl =
    `rustdesk://connection/new/${encodeURIComponent(rustdeskId)}` +
    (password ? `?password=${encodeURIComponent(password)}` : '')

  if (page.askToAccept) {
    return (
      <>
        <div className="rdl-row rdl-ask">
          <span className="rdl-label">Approval</span>
          <span>
            <i className="far fa-hand-point-up" aria-hidden="true"></i> The person at the station will be asked to{' '}
            <strong>Accept</strong> the connection. Password login is off in this mode, so no password is used.
          </span>
        </div>
        {launch?.state === 'ready' ? (
          <a className="btn btn-danger btn-block rdl-open" href={openUrl}>
            <i className="fa fa-mouse-pointer" aria-hidden="true"></i> Open in RustDesk
          </a>
        ) : (
          <button type="button" className="btn btn-danger btn-block rdl-start" disabled={busy} onClick={start}>
            <i className="fa fa-mouse-pointer" aria-hidden="true"></i> Connect
          </button>
        )}
        {error !== '' && <div className="alert alert-danger rdl-error">{error}</div>}
        <div className="rdl-note">
          The button opens the RustDesk client installed on this computer. If nothing happens, open RustDesk yourself and
          enter the ID above — then wait for the person at the station to accept.
        </div>
      </>
    )
  }

  return (
    <>
      <div className="rdl-row rdl-password">
        <span className="rdl-label">Password</span>
        {password !== null ? (
          <>
            <Secret value={password} id="rdlPass" wide />
            <CopyButton text={password} label="Copy" />
          </>
        ) : launch === null ? (
          <span className="text-muted">one-time, valid {Math.round(page.oneTimePassword.ttlSeconds / 60)} minutes</span>
        ) : launch.state === 'expired' ? (
          <span className="text-warning rdl-otp-state">expired unused — request a new one</span>
        ) : launch.state === 'failed' ? (
          <span className="text-danger rdl-otp-state">{launch.message || 'the station could not set it'}</span>
        ) : launch.state === 'revealed' || launch.state === 'closed' ? (
          <span className="text-muted rdl-otp-state">already handed out — request a new one</span>
        ) : (
          <span className="text-muted rdl-otp-state">
            <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> waiting for the station to set it…
          </span>
        )}
      </div>

      {page.stealth.allowed && password === null && !waiting && (
        <div className="rdl-row rdl-stealth">
          <span className="rdl-label">Stealth</span>
          <label style={{ fontWeight: 'normal', margin: 0 }}>
            <input type="checkbox" checked={stealth} onChange={(e) => setStealth(e.target.checked)} /> Connect without
            notifying the user (allowed for your role)
          </label>
        </div>
      )}

      {slow && waiting && (
        <div className="alert alert-warning rdl-slow">
          The station has not picked up the one-time password yet. It may be offline, or its agent may be too old to
          support one-time passwords.
        </div>
      )}
      {error !== '' && <div className="alert alert-danger rdl-error">{error}</div>}

      {password !== null ? (
        <a className="btn btn-danger btn-block rdl-open" href={openUrl}>
          <i className="fa fa-mouse-pointer" aria-hidden="true"></i> Open in RustDesk
        </a>
      ) : (
        <button type="button" className="btn btn-danger btn-block rdl-start" disabled={busy || waiting} onClick={start}>
          <i className="fas fa-key" aria-hidden="true"></i> Get one-time password
        </button>
      )}

      <div className="rdl-note">
        The password works for one session: it expires if no session starts within{' '}
        {Math.round(page.oneTimePassword.ttlSeconds / 60)} minutes, and the station changes it when the session ends. It
        is shown only here, only once. The button opens the RustDesk client installed on this computer; if nothing
        happens, open RustDesk yourself and enter the ID and password above.
      </div>
    </>
  )
}

/**
 * Plasa pentru agentii vechi: statia nu stie inca parola de unica folosinta,
 * iar firma tine parola permanenta - pagina arata parola statiei, ca inainte
 * de 12 octombrie 2026.
 */
function PermanentPassword({ page, rustdeskId }: { page: Page; rustdeskId: string }) {
  const password = page.password ?? null
  return (
    <>
      <div className="rdl-row rdl-password">
        <span className="rdl-label">Password</span>
        {password === null || password === '' ? (
          <span className="text-muted">not reported yet</span>
        ) : (
          <>
            <Secret value={password} id="rdlPass" wide />
            <CopyButton text={password} label="Copy" />
          </>
        )}
      </div>

      <a className="btn btn-danger btn-block rdl-open" href={`rustdesk://connection/new/${encodeURIComponent(rustdeskId)}`}>
        <i className="fa fa-mouse-pointer" aria-hidden="true"></i> Open in RustDesk
      </a>

      <div className="rdl-note rdl-legacy">
        This station&apos;s agent does not support one-time passwords yet, so its permanent password is shown. The
        button opens the RustDesk client installed on this computer. If nothing happens, open RustDesk yourself and
        enter the ID and password above.
      </div>
    </>
  )
}

function Body({ page, onInstalled }: { page: Page; onInstalled(): void }) {
  const rustdeskId = page.identity?.rustdeskId ?? ''
  const hasIdentity = rustdeskId !== ''

  if (page.accessDisabled) {
    return (
      <div className="alert alert-danger rdl-disabled" style={{ marginBottom: 0 }} role="alert">
        <i className="fas fa-ban" aria-hidden="true"></i> <strong>Remote access is disabled for this station.</strong>
        <br />
        Someone turned off &quot;Enable remote access&quot; in the station's Remote Permissions tab. Turn it back on
        there if this connection should be allowed.
      </div>
    )
  }

  if (!hasIdentity) {
    return (
      <div className="alert alert-warning rdl-no-identity" style={{ marginBottom: 0 }} role="alert">
        <i className="fas fa-hourglass-half" aria-hidden="true"></i>{' '}
        <strong>This station has not reported its RustDesk identity yet.</strong>
        <br />
        The RustDesk client on the station registers itself through the agent (<code>POST /rustdesk/identity</code>).
        Until that first report arrives there is no ID to connect to. Check that the station is on and that its agent
        includes the RustDesk piece.
        {/* RustDesk lipsa: cererea de instalare pleaca singura (omul a apasat
            deja Connect), progresul se arata aici, iar cand vine ID-ul pagina
            se reincarca cu butonul "Open in RustDesk". */}
        <RustdeskInstallWait deviceId={page.device.id} onInstalled={onInstalled} />
      </div>
    )
  }

  return (
    <>
      <div className="rdl-row">
        <span className="rdl-label">RustDesk ID</span>
        <span className="rdl-id" id="rdlId">
          {rustdeskId}
        </span>
        <CopyButton text={rustdeskId} label="Copy" />
      </div>

      {page.adminAccounts.length > 0 && (
        /*
         * Cerinta 64: conectarea si cu credentiale de administrator separate.
         * Parola contului NU se mai arata (sta in seif): ridicarea drepturilor
         * se cere omului de la statie (Request elevation in RustDesk) sau se
         * face prin comenzile agentului.
         */
        <div className="rdl-admins">
          {page.adminAccounts.map((account, i) => (
            <AdminAccount key={`${account.username}-${i}`} account={account} />
          ))}
        </div>
      )}

      {page.passwordMode === 'permanent' ? (
        <PermanentPassword page={page} rustdeskId={rustdeskId} />
      ) : (
        <OneTimePassword page={page} rustdeskId={rustdeskId} />
      )}
    </>
  )
}

export function RustdeskLaunchPage({ deviceId, ticketId }: { deviceId: number; ticketId: number }) {
  const { data, error, refetch } = useQuery(rustdeskLaunchQuery(deviceId, ticketId))

  useEffect(() => {
    if (data) document.title = `${data.pageTitle} - OptimumDesk`
  }, [data])

  if (error instanceof ApiError && error.status === 403) return <Forbidden />
  if (error) {
    /* 404 - masina nu e in firmele omului - se spune ca in portal: ca si cum
       n-ar exista. Nu se confirma id-uri straine. */
    return (
      <div id="content">
        <div className="rdl-wrap">
          <div className="alert alert-danger" role="alert">
            {error instanceof ApiError && error.status === 404 ? 'No such device.' : `Something went wrong: ${error.message}`}
          </div>
        </div>
      </div>
    )
  }
  if (!data) return <Spinner />

  const identity = data.identity

  return (
    <div id="content">
      <div className="rdl-wrap">
        <div className="rdl-card">
          <div className="rdl-device">
            <i className="fas fa-bolt color-yellow" aria-hidden="true"></i> {data.device.name}
          </div>
          <div className="rdl-meta">
            {identity && identity.hostname !== '' && (
              <>
                {identity.hostname}
                {identity.ip !== '' && <> &middot; {identity.ip}</>}
                {' '}&middot;{' '}
              </>
            )}
            {identity && identity.lastReportedAt !== '' && <>last reported {identity.lastReportedAt}</>}
            {data.ticket !== null && data.ticket > 0 && <> &middot; for ticket #{data.ticket}</>}
          </div>

          <Body page={data} onInstalled={() => void refetch()} />

          {data.kits.length > 0 && (
            <div className="rdl-note rdl-kits">
              No RustDesk on this computer, or yours cannot find the station? Download it for:{' '}
              <span className="rdl-kits-list">
                {data.kits.map((kit, i) => (
                  <span key={kit.url}>
                    {i > 0 ? ' · ' : ''}
                    <a href={kit.url} title={kit.hint}>
                      <i className={kit.icon} aria-hidden="true"></i> {kit.label}
                    </a>
                  </span>
                ))}
              </span>
              <br />
              The Windows client comes pre-configured for this server — run it exactly as downloaded, renaming the file
              strips the configuration. On Ubuntu and macOS, set the ID server and key once, in RustDesk Settings →
              Network.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
